import { useEffect, useMemo, useState } from "react";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	useJournalEntries,
	useJournals,
	usePostManualEntry,
	useReverseEntry,
} from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";
import { apiError, Field, inputStyle } from "./_ui";

interface ODLine {
	account: string;
	debit: number;
	credit: number;
	label: string;
}

/** FIN-05 — Journaux SYSCOHADA, écritures, saisie OD, contrepassation. */
export function AdminJournalsPage() {
	const { data: journals } = useJournals();
	const [code, setCode] = useState<string>("VTE");
	const { data: entries, isLoading } = useJournalEntries(code);
	const post = usePostManualEntry();
	const reverse = useReverseEntry();
	const [msg, setMsg] = useState<{
		kind: "danger" | "info";
		text: string;
	} | null>(null);
	const [odOpen, setOdOpen] = useState(false);
	const [od, setOd] = useState({
		postedAt: new Date().toISOString().slice(0, 10),
		label: "",
		reference: "",
	});
	const [odLines, setOdLines] = useState<ODLine[]>([
		{ account: "", debit: 0, credit: 0, label: "" },
		{ account: "", debit: 0, credit: 0, label: "" },
	]);

	useEffect(() => {
		if (journals?.length && !journals.find((j) => j.code === code))
			setCode(journals[0].code);
	}, [journals, code]);

	const odTotals = useMemo(() => {
		const d = odLines.reduce((s, l) => s + (l.debit || 0), 0);
		const c = odLines.reduce((s, l) => s + (l.credit || 0), 0);
		return { d, c, balanced: d === c && d > 0 };
	}, [odLines]);

	const submitOd = () => {
		setMsg(null);
		post.mutate(
			{
				postedAt: od.postedAt,
				label: od.label,
				reference: od.reference || undefined,
				lines: odLines
					.filter((l) => l.account && (l.debit || l.credit))
					.map((l) => ({
						account: l.account,
						debit: l.debit || undefined,
						credit: l.credit || undefined,
						label: l.label || undefined,
					})),
			},
			{
				onSuccess: () => {
					setMsg({ kind: "info", text: "Écriture OD enregistrée." });
					setOdOpen(false);
					setOdLines([
						{ account: "", debit: 0, credit: 0, label: "" },
						{ account: "", debit: 0, credit: 0, label: "" },
					]);
					setOd({ ...od, label: "", reference: "" });
				},
				onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
			},
		);
	};

	const [reverseId, setReverseId] = useState<string | null>(null);
	const [reverseReason, setReverseReason] = useState("");

	const openReverse = (id: string) => {
		setReverseReason("");
		setReverseId(id);
	};
	const submitReverse = () => {
		if (!reverseId || reverseReason.trim().length === 0) return;
		reverse.mutate(
			{ id: reverseId, reason: reverseReason.trim() },
			{
				onSuccess: () => {
					setMsg({ kind: "info", text: "Écriture contrepassée." });
					setReverseId(null);
				},
				onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
			},
		);
	};

	return (
		<>
			<PageHead
				title="Journaux comptables"
				sub="Livre-journal SYSCOHADA · chronologique, non modifiable après clôture"
				actions={
					<AdminButton variant="primary" onClick={() => setOdOpen((v) => !v)}>
						<AdminIcon n="plus" s={14} /> Écriture OD
					</AdminButton>
				}
			/>

			{msg && (
				<AlertBanner
					variant={msg.kind === "danger" ? "danger" : "info"}
					title={msg.text}
				/>
			)}

			{odOpen && (
				<Card style={{ marginBottom: 16 }}>
					<CardHead
						title="Saisie d'une écriture (Opérations Diverses)"
						sub="Débit = Crédit obligatoire"
					/>
					<CardBody>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
								gap: 10,
								marginBottom: 10,
							}}
						>
							<Field label="Date">
								<input
									type="date"
									style={inputStyle}
									value={od.postedAt}
									onChange={(e) => setOd({ ...od, postedAt: e.target.value })}
								/>
							</Field>
							<Field label="Libellé">
								<input
									style={inputStyle}
									value={od.label}
									onChange={(e) => setOd({ ...od, label: e.target.value })}
								/>
							</Field>
							<Field label="Référence">
								<input
									style={inputStyle}
									value={od.reference}
									onChange={(e) => setOd({ ...od, reference: e.target.value })}
								/>
							</Field>
						</div>
						<div className="table-wrap" style={{ margin: 0 }}>
							<table className="table">
								<thead>
									<tr>
										<th>Compte</th>
										<th>Libellé ligne</th>
										<th style={{ width: 130 }}>Débit</th>
										<th style={{ width: 130 }}>Crédit</th>
									</tr>
								</thead>
								<tbody>
									{odLines.map((l, i) => (
										<tr key={i}>
											<td>
												<input
													style={inputStyle}
													placeholder="ex. 571"
													value={l.account}
													onChange={(e) =>
														setOdLines((ls) =>
															ls.map((x, j) =>
																j === i ? { ...x, account: e.target.value } : x,
															),
														)
													}
												/>
											</td>
											<td>
												<input
													style={inputStyle}
													value={l.label}
													onChange={(e) =>
														setOdLines((ls) =>
															ls.map((x, j) =>
																j === i ? { ...x, label: e.target.value } : x,
															),
														)
													}
												/>
											</td>
											<td>
												<input
													type="number"
													style={inputStyle}
													value={l.debit}
													onChange={(e) =>
														setOdLines((ls) =>
															ls.map((x, j) =>
																j === i
																	? { ...x, debit: Number(e.target.value) }
																	: x,
															),
														)
													}
												/>
											</td>
											<td>
												<input
													type="number"
													style={inputStyle}
													value={l.credit}
													onChange={(e) =>
														setOdLines((ls) =>
															ls.map((x, j) =>
																j === i
																	? { ...x, credit: Number(e.target.value) }
																	: x,
															),
														)
													}
												/>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginTop: 12,
								flexWrap: "wrap",
								gap: 8,
							}}
						>
							<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
								<button
									className="btn-ghost btn-sm"
									onClick={() =>
										setOdLines((ls) => [
											...ls,
											{ account: "", debit: 0, credit: 0, label: "" },
										])
									}
								>
									<AdminIcon n="plus" s={12} /> Ligne
								</button>
								<Tag variant={odTotals.balanced ? "success" : "warning"} noDot>
									D {fmtFCFA(odTotals.d)} / C {fmtFCFA(odTotals.c)}
								</Tag>
							</div>
							<AdminButton
								variant="primary"
								onClick={submitOd}
								disabled={!odTotals.balanced || !od.label || post.isPending}
							>
								<AdminIcon n="check" s={14} /> Enregistrer
							</AdminButton>
						</div>
					</CardBody>
				</Card>
			)}

			<Card>
				<CardBody>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: 8,
							marginBottom: 12,
						}}
					>
						{(journals ?? []).map((j) => (
							<ChipFilter
								key={j.code}
								on={code === j.code}
								onClick={() => setCode(j.code)}
								count={undefined}
							>
								{j.code} · {j.label}
							</ChipFilter>
						))}
					</div>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>N°</th>
									<th>Date</th>
									<th>Référence</th>
									<th>Libellé</th>
									<th className="right">Débit</th>
									<th className="right">Crédit</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{isLoading && (
									<tr>
										<td
											colSpan={7}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Chargement…
										</td>
									</tr>
								)}
								{!isLoading && (entries?.length ?? 0) === 0 && (
									<tr>
										<td
											colSpan={7}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Aucune écriture dans ce journal.
										</td>
									</tr>
								)}
								{(entries ?? []).map((e) => (
									<tr key={e.id}>
										<td className="mono">
											{e.journalCode}-{e.entryNo}
										</td>
										<td className="muted" style={{ fontSize: 12 }}>
											{e.postedAt}
										</td>
										<td className="mono" style={{ fontSize: 12 }}>
											{e.reference ?? "—"}
										</td>
										<td>
											{e.label}
											{e.reversedEntryId && (
												<Tag variant="neutral" noDot style={{ marginLeft: 6 }}>
													contrepassée
												</Tag>
											)}
										</td>
										<td className="right tn">{fmtFCFA(e.totalDebit)}</td>
										<td className="right tn">{fmtFCFA(e.totalCredit)}</td>
										<td>
											{!e.reversedEntryId && (
												<button
													className="btn-ghost btn-sm"
													onClick={() => openReverse(e.id)}
													title="Contrepasser"
												>
													<AdminIcon n="refresh" s={13} />
												</button>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardBody>
			</Card>

			{reverseId && (
				<Modal
					title="Contrepasser l'écriture"
					subtitle="Une écriture inverse sera générée. L'écriture d'origine reste conservée (SYSCOHADA)."
					onClose={() => setReverseId(null)}
				>
					<form
						onSubmit={(ev) => {
							ev.preventDefault();
							submitReverse();
						}}
					>
						<Field label="Motif de la contrepassation">
							<textarea
								autoFocus
								style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
								value={reverseReason}
								onChange={(ev) => setReverseReason(ev.target.value)}
								maxLength={300}
								placeholder="Ex. erreur d'imputation sur le compte 571…"
							/>
						</Field>
						<ModalFooter>
							<button
								type="button"
								className="btn btn-ghost"
								onClick={() => setReverseId(null)}
							>
								Annuler
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={
									reverseReason.trim().length === 0 || reverse.isPending
								}
								style={{
									opacity:
										reverseReason.trim().length === 0 || reverse.isPending
											? 0.5
											: 1,
									cursor:
										reverseReason.trim().length === 0
											? "not-allowed"
											: "pointer",
								}}
							>
								{reverse.isPending ? "Contrepassation…" : "Contrepasser"}
							</button>
						</ModalFooter>
					</form>
				</Modal>
			)}
		</>
	);
}
